import { FunctionComponent, SyntheticEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';
import { useConfig } from '~components/hooks';
import { useYaMetrica } from '~frontend/provider/yametrica';
import playAudio from '~frontend/utils/playAudio';
import { Button } from '~shared/frontend/ui';

import { gql, useMutation, useQuery } from '@apollo/client';

import Currency from '../Currency';
import { Loader } from '../Loader';
import WheelTimer from './WheelTimer';
import _ from 'lodash';

const Set_Secret_Key = gql`
  mutation setSecretKey($code: String!) {
    setSecretKey(code: $code) {
      id
      getWheelLetters
      getWheelStep
      lastWheelGame
    }
  }
`;

const SET_SECRET_CODE = gql`
  mutation setSecretCode($code: String!) {
    setSecretCode(code: $code) {
      id
      getWheelStep
      lastWheelGame
    }
  }
`;

interface CircleControlsProps {
  onSignWheel: (event: any) => Promise<void>; // Define the prop type for onSignWheel
}

const CircleControls: FunctionComponent<CircleControlsProps> = ({
  onSignWheel,
}) => {
  const getConfig = useConfig();
  const letters = ['C', 'S', 'C', 'A', 'S', 'E'];
  const codeValueRef = useRef(null);
  const secretCodeValueRef = useRef(null);
  const [secretKeyIsOpen, setSecretKeyButton] = useToggle(false);
  const [secretCodeIsOpen, setSecretCodeButton] = useToggle(false);
  const { t } = useTranslation();
  const yametrica = useYaMetrica();

  const { loading, error, data } = useQuery(
    gql`
      query CircleControls {
        getProfile {
          id
          getWheelLetters
          getWheelStep
          lastWheelGame
          battlePassCoins
          wheelCoins
        }
      }
    `,
  );

  const [setSecretKey, { loading: loadingMutation }] =
    useMutation(Set_Secret_Key);

  const [setSecretCode, { loading: loadingCodeMutation }] =
    useMutation(SET_SECRET_CODE);

  const reachGoal = useCallback(
    (
      event: SyntheticEvent<HTMLElement>,
      { reachgoal } = event?.currentTarget?.dataset ||
        (event.target as HTMLElement)?.dataset,
    ) => {
      yametrica.reachGoal(reachgoal);
    },
    [],
  );

  const activateSecretKey = useCallback(async (event) => {
    event.preventDefault();

    if (loadingMutation) {
      return false;
    }

    try {
      await setSecretKey({
        variables: {
          code: codeValueRef.current.value,
        },
      });

      setSecretKeyButton(false);

      toast.success(
        <>
          <div className="notify-title">{t('success')}!</div>
          <div className="notify-text">{t('Your code has been activated')}</div>
        </>,
      );
    } catch (error) {
      toast.error(
        <>
          <div className="notify-title">{t('Error')}!</div>
          <div className="notify-text">{t(error.message)}</div>
        </>,
      );
    }
  }, []);

  const onSubscribe = (event: SyntheticEvent<HTMLElement>) => {
    const dataEvent = event.currentTarget.getAttribute('data-event');

    if (dataEvent) {
      window?.dataLayer?.push({
        event: dataEvent,
      });
    }
  };

  const activateSecretCode = useCallback(async (event) => {
    event.preventDefault();

    if (loadingCodeMutation) {
      return false;
    }

    try {
      if (_.includes('vk', getConfig.seo.wheelGroup)) {
        window.open(
          'https://vk.com/widget_community.php?act=a_subscribe_box&oid=-152839667&state=1&widget=4' ||
            getConfig?.seo.vk,
          '_blank',
        );
      } else if (_.includes('fb', getConfig.seo.wheelGroup)) {
        window.open(getConfig?.seo.fb, '_blank');
      } else if (getConfig?.isRU) {
        window.open(getConfig?.seo.telegram, '_blank');
      }

      await setSecretCode({
        variables: {
          code: secretCodeValueRef?.current?.value || '',
        },
      });

      onSubscribe(event);

      setSecretCodeButton(false);
    } catch (error) {
      toast.error(
        <>
          <div className="notify-title">{t('Error')}!</div>
          <div className="notify-text">{t(error.message)}</div>
        </>,
      );
    }
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <>CircleControls | Error! {error.message}</>;
  }

  const { getProfile } = data;

  return (
    <div className="circle-controls">
      <header className="bonus-header">
        <h1>{t('Free wheel')}</h1>
        <div className="title-sub">
          {/* {getProfile?.getWheelStep === 4
            ? t('Until the free reel opening is left:')
            : getProfile?.getWheelStep === 5
            ? t('Press the button and spin the bonus wheel')
            : t('Pull the lever to claim your bonus!')} */}
        </div>

        {/* {getProfile?.getWheelStep === 4 && (
          <>
            <WheelTimer lastWheelGame={getProfile.lastWheelGame} />
            <div>{t('or')}</div>
          </>
        )} */}

        <div className="bonus-header-btns">
          {/* {!getProfile &&
            (getConfig?.isEU ? (
              <a
                href="/auth/steam"
                className="btn-bonus-login"
                id="vk_login"
                data-reachgoal="wheel_login"
                onClick={reachGoal}
                onMouseEnter={playAudio}
                data-audio="3.mp3"
                data-volume="0.15"
              >
                {t('Authorize')}
              </a>
            ) : (
              <a
                href="/auth/steam"
                className="btn-bonus-login"
                id="vk_login"
                data-reachgoal="wheel_login"
                onClick={reachGoal}
                onMouseEnter={playAudio}
                data-audio="3.mp3"
                data-volume="0.15"
              >
                <span className="text">{t('Authorize')}</span>
              </a>
            ))} */}

          {!getProfile && (
            <a
              href="/auth/steam"
              className="btn-bonus-login"
              id="vk_login"
              data-reachgoal="wheel_login"
              onClick={reachGoal}
              onMouseEnter={playAudio}
              data-audio="3.mp3"
              data-volume="0.15"
            >
              {t('Authorize')}
            </a>
          )}

          {getProfile &&
            (getProfile?.wheelCoins < getConfig?.wheelSpinCost ? (
              <a href="#" className="btn-circle-buy disabled">
                <span className="text">{t('Game_costs')}</span>
                <span className="cost">
                  {getConfig?.wheelSpinCost}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.99998 13.917C11.8377 13.441 14 10.973 14 8C14 5.027 11.8377 2.55903 8.99998 2.08296V3.99994L9.99998 3.99994L12 5.99994H6.99998L5.99998 6.99994V8.99994L6.99998 9.99994H12L9.99998 11.9999H5.99998L3.99998 9.99994V5.99994L5.99998 3.99994L6.99998 3.99994V2.08296C4.16228 2.55905 2 5.02701 2 8C2 10.973 4.16228 13.441 6.99998 13.917V12H8.99998V13.917Z"
                      fill="white"
                    ></path>
                  </svg>
                </span>
              </a>
            ) : (
              <a href="#" className="btn-circle-buy" onMouseDown={onSignWheel}>
                <span className="text">{t('Wheel_spin')}</span>
                <span className="cost">
                  {getConfig?.wheelSpinCost}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM8.99998 13.917C11.8377 13.441 14 10.973 14 8C14 5.027 11.8377 2.55903 8.99998 2.08296V3.99994L9.99998 3.99994L12 5.99994H6.99998L5.99998 6.99994V8.99994L6.99998 9.99994H12L9.99998 11.9999H5.99998L3.99998 9.99994V5.99994L5.99998 3.99994L6.99998 3.99994V2.08296C4.16228 2.55905 2 5.02701 2 8C2 10.973 4.16228 13.441 6.99998 13.917V12H8.99998V13.917Z"
                      fill="white"
                    ></path>
                  </svg>
                </span>
              </a>
            ))}
          <div className="circle-user-coins-mob">
            <div className="circle-user-coins-mob-val">
              <div className="circle-user-coins-mob-val-text">
                {t('Balance')}:
              </div>
              <div className="circle-user-coins-mob-val-number">
                {getProfile?.wheelCoins}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.99902 12V8.00097H9.99926V12H6.99902ZM12.0002 12V8.00097H15.0002V12H12.0002ZM12.0002 20.001V17.001H9.99926V20.001H6.99902V17.001H3.99902L0 12.999V6.99902L3.99902 3H6.99902V-0.000976562H9.99926V3H12.0002V-0.000976562H15.0002V3H18.0002L19.9985 6H5.00097L2.99999 8.00098V12L5.00097 14.001H19.9985L18.0002 17.001H15.0002V20.001L12.0002 20.001Z"
                    fill="#F59EFF"
                  ></path>
                </svg>
              </div>
            </div>
            <div className="vl"></div>
            <div className="circle-user-coins-mob-cost">
              <div className="circle-user-coins-mob-cost-text">
                {t('Before_bonus')}:
              </div>
              <div className="circle-user-coins-mob-cost-number">
                {getProfile?.wheelCoins < getConfig?.wheelSpinCost ? (
                  <>{getConfig?.wheelSpinCost - getProfile?.wheelCoins}</>
                ) : (
                  <>0 </>
                )}
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.99902 12V8.00097H9.99926V12H6.99902ZM12.0002 12V8.00097H15.0002V12H12.0002ZM12.0002 20.001V17.001H9.99926V20.001H6.99902V17.001H3.99902L0 12.999V6.99902L3.99902 3H6.99902V-0.000976562H9.99926V3H12.0002V-0.000976562H15.0002V3H18.0002L19.9985 6H5.00097L2.99999 8.00098V12L5.00097 14.001H19.9985L18.0002 17.001H15.0002V20.001L12.0002 20.001Z"
                    fill="#F59EFF"
                  ></path>
                </svg>
              </div>
            </div>
          </div>

          {/* {: getProfile?.getWheelStep === 2 ? (
            <a
              target="_blank"
              rel="noreferrer noopener"
              href="https://vk.com/widget_community.php?act=a_subscribe_box&oid=-152839667&state=1&widget=4"
              className="circle-how-play"
              id="vk_subscribe"
              data-event="wheel_subscribe_VK"
              //  onClick={checkSubscribe}
              onClick={onSubscribe}
            >
              {t('Subscribe to')}
            </a>
          ) : getProfile?.getWheelStep === 5 ? (
            getConfig?.isEU ? (
              <a
                target="_blank"
                rel="noreferrer noopener"
                href="https://m.me/173484216691359"
                className="circle-how-play"
                id="senler_subscribe"
                data-event="wheel_subscribe_NEWS"
                //  onClick={checkSubscribe}
                onClick={onSubscribe}
              >
                {t('Subscribe to news')}
              </a>
            ) : (
              <Button
                loading={loadingCodeMutation}
                className="circle-how-play"
                id="senler_subscribe"
                data-event="wheel_subscribe_TG"
                onClick={activateSecretCode}
              >
                {_.includes('telegram', getConfig.seo.wheelGroup)
                  ? t('Subscribe to Telegram')
                  : t('Subscribe to news')}
              </Button>
            )
          ) : (
            getProfile?.getWheelStep === 3 && (
              <NavLink to="/faq?id=9" className="circle-how-play">
                {t('How to play?')}
              </NavLink>
            )
          )}     */}

          {/* {: secretKeyIsOpen ? (
            <button className="btn-circle" onClick={activateSecretKey}>
              {t('Activate')}
            </button>
          ) : (
            <button className="btn-circle" onClick={setSecretKeyButton}>
              {t('Enter the secret code')}
            </button>
          )} */}
          {/* {getProfile?.getWheelStep === 4 && secretKeyIsOpen && (
            <input
              type="text"
              placeholder={t('Enter your secret key')}
              ref={codeValueRef}
            />
          )} */}
          {/* {getConfig?.isEU && getProfile?.getWheelStep === 5 ? (
            secretCodeIsOpen ? (
              <>
                <button className="btn-circle" onClick={activateSecretCode}>
                  {t('Activate')}
                </button>
                <input
                  type="text"
                  placeholder={t('bonus_wheel_enter_activation_button')}
                  ref={secretCodeValueRef}
                />
              </>
            ) : (
              <button className="btn-circle" onClick={setSecretCodeButton}>
                {t('bonus_wheel_enter_activation_text')}
              </button>
            )
          ) : null} */}
        </div>
      </header>

      {/* <div className="circle-super-bonus">
        {t('Super Bonus')} -{' '}
        {getConfig?.getCurrency.char === 'RUB' ? '10 000' : '100'} <Currency />
      </div>
      <div className="circle-super-bonus-title">
        {t('Collect the word')} CSCASE
      </div>
      <div className="circle-words">
        {letters.map((item, index) => (
          <div
            className={
              getProfile?.getWheelLetters.includes(item)
                ? 'circle-words-item active'
                : 'circle-words-item'
            }
            key={index}
          >
            {item}
          </div>
        ))}
      </div> */}
    </div>
  );
};

export default CircleControls;
